import { format } from 'date-fns';

export const mergeEventsForDay = (events, date) => {
  const sameDay = events
    .filter((e) => {
      const d = new Date(e.start_time);
      return (
        d.getFullYear() === date.getFullYear() &&
        d.getMonth() === date.getMonth() &&
        d.getDate() === date.getDate()
      );
    })
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

  const merged = [];
  for (const e of sameDay) {
    if (!merged.length) {
      merged.push({ ...e });
      continue;
    }
    const last = merged[merged.length - 1];
    const s = new Date(e.start_time),
      le = new Date(last.end_time);
    if (s <= le) {
      if (new Date(e.end_time) > le) last.end_time = e.end_time;
    } else {
      merged.push({ ...e });
    }
  }
  return merged;
};

export const buildBusyMap = (mergedEvents, timeSlots, dayDate, bufferMinutes = 30) => {
  const busy = new Array(timeSlots.length).fill(false);
  mergedEvents.forEach((ev) => {
    const s = new Date(ev.start_time),
      e = new Date(ev.end_time);

    // Add buffer time before and after the event
    const bufferedStart = new Date(s.getTime() - bufferMinutes * 60 * 1000);
    const bufferedEnd = new Date(e.getTime() + bufferMinutes * 60 * 1000);

    timeSlots.forEach((t, i) => {
      const [h, m] = t.split(':').map(Number);
      const slotStart = new Date(dayDate);
      slotStart.setHours(h, m, 0, 0);
      const slotEnd = new Date(slotStart.getTime() + 30 * 60 * 1000);

      // Mark as busy if slot overlaps with the buffered event time
      if (bufferedStart < slotEnd && bufferedEnd > slotStart) busy[i] = true;
    });
  });
  return busy;
};

export const findFreeGaps = (busyMap) => {
  const gaps = [];
  let i = 0;
  while (i < busyMap.length) {
    while (i < busyMap.length && busyMap[i]) i++;
    if (i >= busyMap.length) break;
    const start = i;
    while (i < busyMap.length && !busyMap[i]) i++;
    const end = i - 1; // inclusive
    if (end >= start) gaps.push({ startIndex: start, endIndex: end });
  }
  return gaps;
};

export const formatEventTime = (date) => {
  return format(new Date(date), 'HH:mm');
};

export const getEventDuration = (start, end) => {
  const startTime = new Date(start);
  const endTime = new Date(end);
  const diffMs = endTime - startTime;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (diffHours > 0) {
    return `${diffHours}h ${diffMinutes > 0 ? `${diffMinutes}m` : ''}`;
  }
  return `${diffMinutes}m`;
};

// New utility functions for dynamic calendar event blocks

/**
 * Rounds a time to the nearest 30-minute slot
 * @param {Date} date - The date to round
 * @param {string} direction - 'start' to round down, 'end' to round up
 * @returns {Date} - The rounded date
 */
export const roundToNearestSlot = (date, direction = 'start') => {
  const rounded = new Date(date);
  const minutes = rounded.getMinutes();

  if (direction === 'start') {
    // Round down to nearest 30-minute slot
    if (minutes < 30) {
      rounded.setMinutes(0, 0, 0);
    } else {
      rounded.setMinutes(30, 0, 0);
    }
  } else {
    // Round up to nearest 30-minute slot
    if (minutes <= 30) {
      rounded.setMinutes(30, 0, 0);
    } else {
      rounded.setMinutes(0, 0, 0);
      rounded.setHours(rounded.getHours() + 1);
    }
  }

  return rounded;
};

/**
 * Finds the time slot index for a given time
 * @param {Date} time - The time to find
 * @param {Array} timeSlots - Array of time slot strings
 * @param {Date} dayDate - The base date for the day
 * @returns {number} - The index of the time slot, or -1 if not found
 */
export const findTimeSlotIndex = (time, timeSlots, dayDate) => {
  const targetTime = new Date(time);

  for (let i = 0; i < timeSlots.length; i++) {
    const [h, m] = timeSlots[i].split(':').map(Number);
    const slotTime = new Date(dayDate);
    slotTime.setHours(h, m, 0, 0);

    if (targetTime.getTime() === slotTime.getTime()) {
      return i;
    }
  }

  return -1;
};

/**
 * Finds the nearest time slot index for a given time
 * @param {Date} time - The time to find
 * @param {Array} timeSlots - Array of time slot strings
 * @param {Date} dayDate - The base date for the day
 * @param {string} direction - 'start' to find earlier slot, 'end' to find later slot
 * @returns {number} - The index of the nearest time slot
 */
export const findNearestTimeSlotIndex = (time, timeSlots, dayDate, direction = 'start') => {
  const targetTime = new Date(time);

  if (direction === 'start') {
    // Find the latest slot that starts before or at the target time
    for (let i = timeSlots.length - 1; i >= 0; i--) {
      const [h, m] = timeSlots[i].split(':').map(Number);
      const slotTime = new Date(dayDate);
      slotTime.setHours(h, m, 0, 0);

      if (slotTime <= targetTime) {
        return i;
      }
    }
    return 0; // If no slot found, return first slot
  } else {
    // Find the earliest slot that starts after or at the target time
    for (let i = 0; i < timeSlots.length; i++) {
      const [h, m] = timeSlots[i].split(':').map(Number);
      const slotTime = new Date(dayDate);
      slotTime.setHours(h, m, 0, 0);

      if (slotTime >= targetTime) {
        return i;
      }
    }
    return timeSlots.length - 1; // If no slot found, return last slot
  }
};

/**
 * Processes events to create dynamic blocks that align with time slots
 * @param {Array} events - Array of calendar events
 * @param {Array} timeSlots - Array of time slot strings
 * @param {Date} dayDate - The base date for the day
 * @returns {Array} - Array of processed event blocks
 */
export const processEventBlocks = (events, timeSlots, dayDate) => {
  return events.map(event => {
    const eventStart = new Date(event.start_time);
    const eventEnd = new Date(event.end_time);

    // Round start time down to nearest slot
    const roundedStart = roundToNearestSlot(eventStart, 'start');
    const startIndex = findNearestTimeSlotIndex(roundedStart, timeSlots, dayDate, 'start');

    // Round end time up to nearest slot
    const roundedEnd = roundToNearestSlot(eventEnd, 'end');
    const endIndex = findNearestTimeSlotIndex(roundedEnd, timeSlots, dayDate, 'end');

    return {
      ...event,
      originalStart: eventStart,
      originalEnd: eventEnd,
      roundedStart,
      roundedEnd,
      startIndex,
      endIndex,
      displayStart: roundedStart,
      displayEnd: roundedEnd
    };
  });
};

/**
 * Calculates the pixel offset and height for an event block
 * @param {Object} eventBlock - The processed event block
 * @param {Array} timeSlots - Array of time slot strings
 * @param {Date} dayDate - The base date for the day
 * @param {number} slotHeight - Height of each time slot in pixels
 * @returns {Object} - Object with top offset and height in pixels
 */
export const calculateEventBlockDimensions = (eventBlock, timeSlots, dayDate, slotHeight = 20) => {
  const { startIndex, endIndex, originalStart, originalEnd } = eventBlock;

  // Calculate the start time, capped at 07:00
  const dayStartTime = new Date(dayDate);
  dayStartTime.setHours(7, 0, 0, 0);

  const effectiveStartTime = originalStart < dayStartTime ? dayStartTime : originalStart;

  // Calculate the end time, capped at 22:00
  const dayEndTime = new Date(dayDate);
  dayEndTime.setHours(22, 0, 0, 0);

  const effectiveEndTime = originalEnd > dayEndTime ? dayEndTime : originalEnd;

  // Calculate offset from the start of the slot
  const slotStartTime = new Date(dayDate);
  const [h, m] = timeSlots[startIndex].split(':').map(Number);
  slotStartTime.setHours(h, m, 0, 0);

  const minutesFromSlotStart = Math.max(0, (effectiveStartTime - slotStartTime) / (60 * 1000));
  const topOffsetPx = (minutesFromSlotStart / 30) * slotHeight;

  // Calculate duration height based on the capped start and end times
  const durationMs = effectiveEndTime - effectiveStartTime;
  const heightPx = Math.max(2, (durationMs / (30 * 60 * 1000)) * slotHeight); // Add 2px to fill the slot properly

  return {
    topOffsetPx,
    heightPx: heightPx,
    startIndex,
    endIndex
  };
};
